import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { UserApiService } from '../../../../users/services/user-api.service';
import { CrmUserResponse } from '../../../../users/models/user.model';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketTeamMember } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-team-members-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  template: `
    <div class="p-6 space-y-4">
      @if (error) {
        <div class="p-3 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 rounded-lg text-sm text-error-700 dark:text-error-400">{{ error }}</div>
      }
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Usuario <span class="text-error-500">*</span>
            @if (isEditing) {
              <span class="text-xs text-gray-400">(no editable)</span>
            }
          </label>
          <select formControlName="intIdUser"
            class="w-full px-2.5 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed"
            [class.border-error-500]="hasError('intIdUser')"
            [class.border-gray-300]="!hasError('intIdUser')">
            <option [ngValue]="null">-- Elegir usuario --</option>
            @for (u of users; track u.id) {
              <option [ngValue]="u.id">{{ u.fullname }} {{ u.lastname }} — {{ u.mail }} ({{ u.roleName }})</option>
            }
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tickets concurrentes máximos</label>
          <input type="number" formControlName="maxConcurrentTickets" min="0"
            placeholder="Sin límite"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Habilidades <span class="text-gray-400">(separadas por coma, para SKILL_BASED routing)</span>
          </label>
          <input type="text" formControlName="skillsCsv"
            placeholder="facturacion, ingles, redes"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500" />
        </div>
        <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" formControlName="isAvailable" class="rounded border-gray-300 dark:border-gray-600" />
          Disponible
        </label>
        <div class="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">Cancelar</app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) { Guardando… } @else { {{ isEditing ? 'Actualizar' : 'Agregar' }} }
          </app-button>
        </div>
      </form>
    </div>
  `
})
export class TicketsTeamMembersFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketTeamMember>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private userApi = inject(UserApiService);

  isEditing = false;
  itemId: number | null = null;
  parentTeamId: number | null = null;
  saving = false;
  error: string | null = null;

  users: CrmUserResponse[] = [];

  form: FormGroup = this.fb.group({
    intIdUser: [null, [Validators.required]],
    maxConcurrentTickets: [null],
    skillsCsv: [''],
    isAvailable: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.userApi.listActiveByBusiness(businessId)
      .pipe(catchError(() => of([] as CrmUserResponse[])), takeUntil(this.destroy$))
      .subscribe((users) => { this.users = users; });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(parentTeamId: number | null = null): void {
    this.isEditing = false;
    this.itemId = null;
    this.parentTeamId = parentTeamId;
    this.error = null;
    this.saving = false;
    this.form.reset({
      intIdUser: null,
      maxConcurrentTickets: null,
      skillsCsv: '',
      isAvailable: true
    });
    this.form.get('intIdUser')?.enable();
  }

  loadMember(member: TicketTeamMember): void {
    this.isEditing = true;
    this.itemId = member.id;
    this.parentTeamId = member.idTeam;
    this.form.patchValue({
      intIdUser: member.intIdUser,
      maxConcurrentTickets: member.maxConcurrentTickets,
      skillsCsv: (member.skills || []).join(', '),
      isAvailable: member.isAvailable
    });
    this.form.get('intIdUser')?.disable();
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    if (!this.isEditing && this.parentTeamId == null) {
      this.error = 'No se identificó el equipo padre';
      return;
    }
    this.saving = true;
    this.error = null;
    const raw = this.form.getRawValue();
    const skills = (raw.skillsCsv as string || '')
      .split(',').map((s: string) => s.trim()).filter(Boolean);

    const req = {
      intIdUser: Number(raw.intIdUser),
      maxConcurrentTickets: raw.maxConcurrentTickets != null ? Number(raw.maxConcurrentTickets) : undefined,
      skills: skills.length ? skills : undefined,
      isAvailable: !!raw.isAvailable
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateTeamMember(this.itemId, req)
      : this.configApi.addTeamMember(this.parentTeamId!, req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Miembro');
        else this.toast.created('Miembro');
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el miembro';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}